import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import  { pickToken } from '../util/token.util';
import TextField from 'material-ui/TextField';
import { AdminState } from './admin.types';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';
import { AdminBusiness } from './admin.types';
import Phone from 'react-phone-number-input';
import Paper from 'material-ui/Paper';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import { Account } from '../account/Types';
import { isImpersonating } from './Util';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import CommonFeatures from '../commons/CommonFeatures';
import { BusinessState } from 'src/business/business.types';

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly businessState: BusinessState
  readonly toDashboard: () => void
  readonly getBusinessList: () => Promise<Response>
  readonly impersonate: (accountId: number) => Promise<Response>
};

type States = {
  readonly businessList: AdminBusiness[]
  readonly filteredList: AdminBusiness[]
  readonly businessCodeFilter: string
  readonly businessNameFilter: string
  readonly businessPhoneFilter: string
  readonly selectedRow?: AdminBusiness
  readonly modalOpen: boolean
  readonly authToken: string
};

class AdminBusinessComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      businessList: [],
      filteredList: [],
      businessCodeFilter: '',
      businessNameFilter: '',
      businessPhoneFilter: '',
      selectedRow: undefined,
      modalOpen: false,
      authToken: pickToken(props.authState, props.adminState)
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && !isAuthTokenExpired() && nextProps.authState.statusCode === 200) {
      this.setState(
        {
          businessList: nextProps.adminState.businessList,
        },
        () => this.stepsWhenReceivedNextProps(nextProps, this.state)
      );
    }
  }

  componentDidMount() {
    const { getBusinessList } = this.props;

    getBusinessList();
  }

  render() {
    const { filteredList } = this.state;

    return (
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2">
          <div className="col-auto">
            <h3>Business List</h3>
          </div>
        </div>
        <div className="row">
          <div className="col-4">
            <TextField
              hintText="Filter by business code"
              floatingLabelText="Business Code"
              onChange={this.handleBusinessCodeFilterChange}
            />
          </div>
          <div className="col-4">
            <TextField
              hintText="Filter by business name"
              floatingLabelText="Business Name"
              onChange={this.handleBusinessNameFilterChange}
            />
          </div>
          <div className="col-4">
            <TextField
              floatingLabelFixed={true}
              floatingLabelText="Business Phone"
            >
              <Phone
                countries={['CA']}
                onChange={this.handleBusinessPhoneFilterChange}
                convertToNational={true}
                inputStyle={{
                  backgroundColor: 'transparent',
                  borderBottom: '0px',
                  height: 19
                }}
              />
            </TextField>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <Paper zDepth={2}>
              <Table
                height={`${window.innerHeight / 2}px`}
                fixedHeader={true}
                selectable={true}
                multiSelectable={false}
                onCellClick={this.handleCellClick}
              >
                <TableHeader
                  displaySelectAll={false}
                  adjustForCheckbox={false}
                  enableSelectAll={false}
                >
                  <TableRow>
                    <TableHeaderColumn>Code</TableHeaderColumn>
                    <TableHeaderColumn>Name</TableHeaderColumn>
                    <TableHeaderColumn>Phone</TableHeaderColumn>
                  </TableRow>
                </TableHeader>
                <TableBody
                  displayRowCheckbox={false}
                  showRowHover={true}
                >
                  {
                    filteredList.map((row: AdminBusiness, index: number) => (
                      <TableRow key={index} displayBorder={true}>
                        <TableRowColumn>{row.businessCode}</TableRowColumn>
                        <TableRowColumn>{row.name}</TableRowColumn>
                        <TableRowColumn>
                          <Phone
                            countries={['CA']}
                            value={row.phone}
                            disabled={true}
                            onChange={this.doNothing}
                            convertToNational={true}
                            inputStyle={{
                              backgroundColor: 'transparent',
                              borderBottom: '0px',
                              height: 19
                            }}
                          />
                        </TableRowColumn>
                      </TableRow>
                      ))
                  }
                </TableBody>
              </Table>
            </Paper>
          </div>
        </div>
        {this.renderAdminBusinessModal()}
      </CommonFeatures>
    );
  }

  private stepsWhenReceivedNextProps = (nextProps: Props, states: States) => {
    this.filterBusinessList(states.businessList);

    if (isImpersonating(nextProps.adminState)) {
      nextProps.toDashboard();  
    }
  }

  private renderAdminBusinessModal = (): JSX.Element => {
    const { selectedRow, modalOpen } = this.state;
    const accounts = selectedRow ? selectedRow.accounts : [];

    return(
      <Dialog
        title={'Accounts of ' + (selectedRow ? selectedRow.name : '')}
        actions={[this.renderCancelButton()]}
        modal={true}
        contentStyle={{
          width: '80%',
          maxWidth: 'none',
        }}
        open={modalOpen}
      >
        <Paper zDepth={2}>
          <Table
            height={`${window.innerHeight / 2}px`}
            fixedHeader={true}
            fixedFooter={false}
            selectable={true}
            multiSelectable={false}
            onCellClick={(row: number, column: number) => this.handleImpersonateClick(accounts[row].id)}
          >
            <TableHeader
              displaySelectAll={false}
              adjustForCheckbox={false}
              enableSelectAll={false}
            >
              <TableRow>
                <TableHeaderColumn>Email</TableHeaderColumn>
                <TableHeaderColumn>First Name</TableHeaderColumn>
                <TableHeaderColumn>Last Name</TableHeaderColumn>
                <TableHeaderColumn>Account Type</TableHeaderColumn>
              </TableRow>
            </TableHeader>
            <TableBody
              displayRowCheckbox={false}
              showRowHover={true}
            >
              {
                accounts.map((row: Account, index: number) => (
                  <TableRow key={index} displayBorder={true}>
                    <TableRowColumn>{row.email}</TableRowColumn>
                    <TableRowColumn>{row.firstName}</TableRowColumn>
                    <TableRowColumn>{row.lastName}</TableRowColumn>
                    <TableRowColumn>{row.accountType}</TableRowColumn>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </Paper>
      </Dialog>
    );
  }

  private handleCellClick = (row: number, column: number): void => {
    const { filteredList } = this.state;
    const selected = filteredList.filter((value: AdminBusiness, index: number) => index === row);

    this.setState({
      selectedRow: selected.length === 1 ? selected[0] : undefined,
      modalOpen: true
    });
  }

  private doNothing = (newValue: string): void => {
    // Just to suppress warning message coming from react-phone-number-input when onChange is missing
    return;
  }

  private handleImpersonateClick = (accountId: number): void =>
    this.setState(
      {modalOpen: false},
      () => this.props.impersonate(accountId) 
    )

  private filterBusinessList = (businessList: Array<AdminBusiness>): void => {
    const { businessNameFilter, businessCodeFilter, businessPhoneFilter } = this.state;

    this.setState({
      filteredList: businessList
        .filter((adminBusiness: AdminBusiness) => 
          adminBusiness.name.toLowerCase().includes(businessNameFilter.toLowerCase()))
        .filter((adminBusiness: AdminBusiness) =>
          adminBusiness.businessCode.toLowerCase().includes(businessCodeFilter.toLowerCase()))
        .filter((adminBusiness: AdminBusiness) =>
          adminBusiness.phone.toLowerCase().includes(businessPhoneFilter.toLowerCase()))
    });
  }

  private handleBusinessCodeFilterChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState(
      {businessCodeFilter: newValue},
      () => this.filterBusinessList(this.state.businessList)
    )

  private handleBusinessNameFilterChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState(
      {businessNameFilter: newValue},
      () => this.filterBusinessList(this.state.businessList)
    )

  private handleBusinessPhoneFilterChange = (newValue: string): void =>
    this.setState(
      {businessPhoneFilter: newValue},
      () => this.filterBusinessList(this.state.businessList)
    )
  
  private renderCancelButton = (): JSX.Element => {
    return(
      <FlatButton
        label="Cancel"
        primary={true}
        onClick={() => this.setState({modalOpen: false})}
      />
    );
  }
}

export default AdminBusinessComponent;



// WEBPACK FOOTER //
// ./src/admin/admin.business.component.tsx